import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BatteryCharging, Zap, Heart, Shield, Clock, 
  AlertTriangle, CheckCircle, RotateCcw, ChevronLeft, 
  Activity, Play, Flame, Loader2, Brain, ArrowRight
} from 'lucide-react';
import { useAppContext } from '../../core/App';
import { REACTOR_ACTIVITIES, ENTROPY_SCENARIOS, calculateDayResult, calculateBaselineMetrics } from './data/resilienceLogic';
import { db } from '../../core/firebase';
import { doc, updateDoc, serverTimestamp, arrayUnion, getDoc } from 'firebase/firestore';

// --- IMPORTS ---
import ResilienceAudit from './components/ResilienceAudit';

// --- SUB-COMPONENT: ENERGY TANK VISUALIZATION ---
const EnergyTank = ({ current, max = 100 }) => {
  const percentage = Math.max(0, Math.min(100, (current / max) * 100));
  let color = 'bg-emerald-500';
  if (percentage < 30) color = 'bg-red-500 animate-pulse';
  else if (percentage < 60) color = 'bg-yellow-500';

  return (
    <div className="relative w-48 h-48 mx-auto mb-6">
      <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="96" cy="96" r="88"
          stroke="currentColor"
          strokeWidth="12"
          fill="transparent"
          className={`${color.replace('bg-', 'text-')} transition-all duration-1000 ease-out`}
          strokeDasharray={552}
          strokeDashoffset={552 - (552 * percentage) / 100}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Zap size={32} className={`mb-1 ${percentage < 30 ? 'text-red-500' : 'text-slate-400'}`} />
        <span className="text-4xl font-black text-slate-800">{Math.round(current)}%</span>
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Capacity</span>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: AI ANALYSIS VIEW ---
const AnalysisView = ({ analysis, onContinue }) => (
  <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-8">
    <div className="bg-indigo-900 text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 p-8 opacity-10"><Brain size={120} /></div>
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-white/10 rounded-lg"><Activity size={24} className="text-emerald-400" /></div>
          <h2 className="text-2xl font-black uppercase tracking-widest">Diagnostic Report</h2>
        </div>
        
        <div className="bg-black/20 p-6 rounded-2xl border border-white/10 mb-6">
          <h3 className="font-bold text-indigo-200 text-sm uppercase tracking-wider mb-2">Executive Summary</h3>
          <p className="text-lg leading-relaxed font-medium">{analysis.summary}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div className="bg-red-500/20 p-4 rounded-xl border border-red-500/30">
              <h4 className="font-bold text-red-200 text-xs uppercase mb-2 flex items-center gap-2"><AlertTriangle size={14}/> Risk Factors</h4>
              <ul className="text-sm space-y-1 text-red-100 list-disc list-inside">
                 {analysis.risk_factors.map((risk, i) => <li key={i}>{risk}</li>)}
              </ul>
           </div>
           <div className="bg-emerald-500/20 p-4 rounded-xl border border-emerald-500/30">
              <h4 className="font-bold text-emerald-200 text-xs uppercase mb-2 flex items-center gap-2"><CheckCircle size={14}/> Prescription</h4>
              <ul className="text-sm space-y-1 text-emerald-100 list-disc list-inside">
                 {analysis.immediate_advice.map((tip, i) => <li key={i}>{tip}</li>)}
              </ul>
           </div>
        </div>
      </div>
    </div>

    <button onClick={onContinue} className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-lg rounded-2xl shadow-xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2">
       Initialize Reactor Simulation <ArrowRight size={20} />
    </button>
  </div>
);

// --- MAIN CONTROLLER ---
export default function ResilienceReactor() {
  const { goToDashboard, awardCxp, authUser, callApi, budgetData, userProfile } = useAppContext();

  // --- VIEW STATE ---
  const [view, setView] = useState('LOADING'); 
  const [auditAnalysis, setAuditAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // --- DYNAMIC CONFIGURATION SNAPSHOT ---
  // Tracks the initial baseline parameters to detect mid-cycle configuration drift
  const [initialConfig, setInitialConfig] = useState(null);

  // --- DYNAMIC FATIGUE FLOOR CALCULATION ---
  const initialEnergy = useMemo(() => {
    if (!budgetData) return 80; 
    
    const { commuteMode, homeZip, market_class } = budgetData;
    if (commuteMode === 'wfh') return 90; 
    
    // Dynamic Cloud Function evaluation fallback to completely absorb non-cached HCOL codes (e.g. Seattle, Boston)
    const isHcolGrid = market_class === 'HCOL' || 
                      userProfile?.completedModules?.['budget-sim']?.market_class === 'HCOL' ||
                      (homeZip && ['10', '11', '90', '91', '92', '93', '94'].includes(homeZip.substring(0, 2)));
    
    if (commuteMode === 'drive') {
        return isHcolGrid ? 55 : 65; 
    }
    
    return 80; 
  }, [budgetData, userProfile]);

  // --- SIMULATOR STATE ---
  const [stats, setStats] = useState({ 
    energy: initialEnergy, 
    mood: 50, 
    resilience: 20, 
    socialBattery: 50, 
    careerVelocity: 0 
  });
  
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [simulationResult, setSimulationResult] = useState(null);
  const [activeCrisis, setActiveCrisis] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // --- MID-CYCLE DRIFT MONITOR ---
  useEffect(() => {
    if (budgetData && !initialConfig && simulationResult === null) {
      setInitialConfig({
        commuteMode: budgetData.commuteMode,
        homeZip: budgetData.homeZip
      });
    }
  }, [budgetData, initialConfig, simulationResult]);

  const hasConfigDrift = useMemo(() => {
    if (!budgetData || !initialConfig || simulationResult !== null) return false;
    return budgetData.commuteMode !== initialConfig.commuteMode || budgetData.homeZip !== initialConfig.homeZip;
  }, [budgetData, initialConfig, simulationResult]);

  // --- TELEMETRY ORCHESTRATION HOOK ---
  useEffect(() => {
    if (simulationResult === null) {
        setStats(prev => ({
            ...prev,
            energy: initialEnergy
        }));
        console.log(`[Telemetry Sync] Fatigue floor calibrated dynamically to ${initialEnergy}%`);
    }
  }, [initialEnergy, simulationResult]);

  // --- INITIALIZATION ---
  useEffect(() => {
    const checkStatus = async () => {
      if (!authUser) { setView('AUDIT'); return; }
      
      try {
        const userRef = doc(db, 'users', authUser.uid);
        const snap = await getDoc(userRef);
        
        if (snap.exists()) {
          const data = snap.data();
          const psych = data.psychometrics || {};
          
          if (psych.lastAuditDate) {
             setView('SIMULATOR');
          } else {
             setView('AUDIT');
          }
        } else {
          setView('AUDIT');
        }
      } catch (e) {
        console.error("Init Error", e);
        setView('AUDIT');
      }
    };
    checkStatus();
  }, [authUser]);

  // --- AUDIT HANDLER ---
  const handleAuditSubmit = async (inputs) => {
    setIsAnalyzing(true);
    
    try {
        const baseline = calculateBaselineMetrics(inputs);
        
        const systemInstruction = `
            ROLE: High-Performance Coach. 
            TASK: Analyze a candidate's daily load. 
            CONTEXT: User reported: Sleep ${inputs.sleepHours}h, Phone ${inputs.phoneHours}h, Work ${inputs.workHours}h.
            Detected Risks: ${baseline.riskFlags.join(', ')}.
            OUTPUT JSON: {
                "summary": "String (Direct, empathetic but firm assessment of their energy leaks)",
                "risk_factors": ["String (Bullet 1)", "String (Bullet 2)"],
                "immediate_advice": ["String (Actionable fix 1)", "String (Actionable fix 2)"]
            }
        `;
        
        const analysis = await callApi({}, systemInstruction);
        setAuditAnalysis(analysis);
        setView('ANALYSIS');

        if (authUser) {
            const userRef = doc(db, 'users', authUser.uid);
            await updateDoc(userRef, {
                'psychometrics.lastAuditDate': serverTimestamp(),
                'psychometrics.baseline': baseline.stats,
                'psychometrics.riskFlags': baseline.riskFlags
            });
        }

    } catch (e) {
        console.error("Analysis Failed", e);
        setAuditAnalysis({
            summary: "Your schedule shows signs of high cognitive load. We need to optimize your recovery protocols.",
            risk_factors: ["Potential Sleep Deficit", "Digital Fragmentation"],
            immediate_advice: ["Prioritize 8h sleep", "Batch your notifications"]
        });
        setView('ANALYSIS');
    } finally {
        setIsAnalyzing(false);
    }
  };

  // --- SIMULATOR HANDLERS ---
  const projectedStats = calculateDayResult({ ...stats }, selectedActivities);
  const timeUsed = projectedStats.timeUsed;

  const toggleActivity = (actId) => {
    if (selectedActivities.includes(actId)) {
      setSelectedActivities(prev => prev.filter(id => id !== actId));
    } else {
      if (timeUsed + REACTOR_ACTIVITIES[actId].timeCost > 16) {
        alert("Time Cap Reached! You need to sleep.");
        return;
      }
      setSelectedActivities(prev => [...prev, actId]);
    }
  };

  const runSimulation = async () => {
    setIsSimulating(true);
    
    const result = calculateDayResult(stats, selectedActivities);
    const crisisChance = result.stats.resilience < 40 ? 0.6 : 0.2;
    let crisisHit = null;

    if (Math.random() < crisisChance) {
        crisisHit = ENTROPY_SCENARIOS[Math.floor(Math.random() * ENTROPY_SCENARIOS.length)];
        setActiveCrisis(crisisHit);
        result.stats.energy -= crisisHit.impact.energyDrain;
        result.feedback.push(`⚠️ CRISIS: ${crisisHit.title} (-${crisisHit.impact.energyDrain} Energy)`);
        if (result.stats.energy < 0) result.stats.energy = 0;
    }

    await new Promise(resolve => setTimeout(resolve, 1500));

    setStats(result.stats);
    setSimulationResult(result);
    setIsSimulating(false);
    
    if (authUser) {
        try {
            const userRef = doc(db, 'users', authUser.uid);
            await updateDoc(userRef, {
                'psychometrics.currentEnergy': result.stats.energy,
                'psychometrics.currentMood': result.stats.mood,
                'psychometrics.currentResilience': result.stats.resilience,
                'psychometrics.lastCheckIn': serverTimestamp(),
                'psychometrics.history': arrayUnion({
                    timestamp: Date.now(),
                    energyLevel: result.stats.energy,
                    moodLevel: result.stats.mood,
                    resilienceLevel: result.stats.resilience,
                    crisisEncountered: crisisHit ? crisisHit.title : null
                })
            });
            if (result.status === 'stable' && !crisisHit) awardCxp('COMPLETE_PRO_PART'); 
        } catch (e) { console.error("Save failed", e); }
    }
  };

  const resetDay = () => {
    setSimulationResult(null);
    setActiveCrisis(null);
    setSelectedActivities([]);
    setInitialConfig(null); 
    setStats({ 
      energy: initialEnergy, 
      mood: 50, 
      resilience: 20, 
      socialBattery: 50, 
      careerVelocity: 0 
    });
  };

  if (view === 'LOADING' || isAnalyzing) {
      return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-400">
              <Loader2 size={48} className="animate-spin mb-4 text-emerald-500" />
              <p className="font-bold tracking-widest text-xs uppercase">
                  {isAnalyzing ? "AI Performance Coach is Analyzing..." : "Initializing Reactor..."}
              </p>
          </div>
      );
  }

  if (view === 'AUDIT') {
      return (
        <div className="p-6 pb-24">
            <div className="flex items-center mb-8">
                <button onClick={goToDashboard} className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 transition mr-4"><ChevronLeft className="w-6 h-6 text-gray-600" /></button>
                <h1 className="text-2xl font-black text-slate-800">Resilience Reactor</h1>
            </div>
            <ResilienceAudit onSubmit={handleAuditSubmit} />
        </div>
      );
  }

  if (view === 'ANALYSIS' && auditAnalysis) {
      return (
        <div className="p-6 pb-24">
             <AnalysisView analysis={auditAnalysis} onContinue={() => setView('SIMULATOR')} />
        </div>
      );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 animate-fade-in pb-24">
      <div className="flex items-center mb-8">
        <button onClick={goToDashboard} className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 transition mr-4">
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-3">
            <Activity className="text-emerald-500" /> Resilience Reactor
          </h1>
          <p className="text-slate-500 text-sm">Simulation Mode: Active</p>
        </div>
      </div>

      {/* DYNAMIC DRIFT ALERT BANNER */}
      <AnimatePresence>
        {hasConfigDrift && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-6 overflow-hidden col-span-full"
          >
            <div className="p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-900 rounded-r-xl flex items-start gap-3 shadow-inner">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <h4 className="text-xs font-black uppercase tracking-widest text-amber-800">Logistical Drift Detected</h4>
                <p className="text-sm font-medium mt-0.5">
                  Your active budget configuration changed mid-cycle. Hit the <strong>"Reset Timeline"</strong> button to synchronize your cognitive capacity curves with your new geographical footprint.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT COLUMN: DASHBOARD */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white p-6 rounded-3xl shadow-xl border border-slate-100 text-center relative overflow-hidden">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Current Reserves</h3>
            <EnergyTank current={projectedStats.stats.energy} />
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-blue-50 p-3 rounded-2xl border border-blue-100">
                <div className="flex items-center justify-center gap-2 text-blue-600 mb-1"><Heart size={16} /> <span className="font-bold text-xs uppercase">Mood</span></div>
                <div className="text-xl font-black text-slate-800">{Math.round(projectedStats.stats.mood)}</div>
              </div>
              <div className="bg-indigo-50 p-3 rounded-2xl border border-indigo-100">
                <div className="flex items-center justify-center gap-2 text-indigo-600 mb-1"><Shield size={16} /> <span className="font-bold text-xs uppercase">Armor</span></div>
                <div className="text-xl font-black text-slate-800">{Math.round(projectedStats.stats.resilience)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
             <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><Clock size={14} /> Time Budget</h3>
                <span className={`text-sm font-black ${timeUsed > 16 ? 'text-red-500' : 'text-slate-700'}`}>{timeUsed + 8} / 24 hrs</span>
             </div>
             <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden flex">
                <div className="h-full bg-slate-300 w-1/3 border-r border-white" title="Sleep (8h)"></div>
                <div className={`h-full transition-all duration-500 ${timeUsed > 16 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${(timeUsed / 24) * 100}%` }} />
             </div>
             <p className="text-xs text-slate-400 mt-2 text-right">{Math.max(0, 16 - timeUsed)}h "Slack" left</p>
          </div>

          <button onClick={runSimulation} disabled={isSimulating || simulationResult} className={`w-full py-4 rounded-2xl font-black text-lg shadow-lg transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 ${simulationResult ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-500'}`}>
            {isSimulating ? 'Testing...' : (simulationResult ? 'Complete' : 'Run Simulation')}
            {!simulationResult && !isSimulating && <Play size={20} fill="currentColor" />}
          </button>

          {simulationResult && (
             <button onClick={resetDay} className="w-full py-3 bg-white border-2 border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition flex items-center justify-center gap-2"><RotateCcw size={16} /> Reset Timeline</button>
          )}
        </div>

        {/* RIGHT COLUMN: MARKET */}
        <div className="lg:col-span-8">
           <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200 min-h-full">
              <h2 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-2"><BatteryCharging className="text-blue-500" /> Activity Market</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {Object.values(REACTOR_ACTIVITIES).map((act) => {
                    const isSelected = selectedActivities.includes(act.id);
                    const isDisabled = simulationResult !== null;
                    return (
                       <button key={act.id} onClick={() => !isDisabled && toggleActivity(act.id)} disabled={isDisabled} className={`relative p-4 rounded-2xl border-2 text-left transition-all duration-200 group ${isSelected ? 'bg-white border-blue-500 shadow-md ring-4 ring-blue-500/10' : 'bg-white border-transparent hover:border-slate-300 shadow-sm'} ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                          <div className="flex justify-between items-start mb-2">
                             <div className={`p-2 rounded-xl ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}><act.icon size={20} /></div>
                             <div className="text-right"><span className="block text-xs font-bold text-slate-400 uppercase">Cost</span><span className="text-sm font-black text-slate-700">-{act.energyCost} NRG</span></div>
                          </div>
                          <h4 className={`font-bold text-sm mb-1 ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>{act.label}</h4>
                          <p className="text-xs text-slate-500 leading-snug min-h-[2.5em]">{act.description}</p>
                          <div className="mt-3 flex gap-2">
                             {act.resilienceBuff > 0 && <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full flex items-center gap-1"><Shield size={10} /> +{act.resilienceBuff} Armor</span>}
                             {act.lethargyDebuff && <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold rounded-full flex items-center gap-1"><AlertTriangle size={10} /> Lethargy Risk</span>}
                          </div>
                       </button>
                    );
                 })}
              </div>
           </div>
        </div>
      </div>

      <AnimatePresence>
        {(simulationResult && activeCrisis) && (
           <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="fixed bottom-0 left-0 right-0 p-6 z-50 flex justify-center pointer-events-none">
              <div className="bg-red-600 text-white p-6 rounded-3xl shadow-2xl max-w-2xl w-full pointer-events-auto border-4 border-red-800 flex items-start gap-4">
                 <div className="bg-red-800/50 p-3 rounded-2xl"><Flame size={32} className="animate-pulse" /></div>
                 <div>
                    <h2 className="text-2xl font-black uppercase tracking-tight mb-1">Entropy Strike!</h2>
                    <h3 className="font-bold text-red-200 text-lg mb-2">{activeCrisis.title}</h3>
                    <p className="text-white/90 leading-relaxed">{activeCrisis.description}</p>
                    <div className="mt-4 flex gap-3">
                       <span className="px-3 py-1 bg-black/30 rounded-lg text-xs font-bold uppercase">Energy Hit: -{activeCrisis.impact.energyDrain}</span>
                       <span className="px-3 py-1 bg-black/30 rounded-lg text-xs font-bold uppercase">Time Lost: {activeCrisis.impact.timeLost}h</span>
                    </div>
                 </div>
              </div>
           </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(simulationResult && !activeCrisis && simulationResult.status === 'stable') && (
           <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="fixed bottom-0 left-0 right-0 p-6 z-50 flex justify-center pointer-events-none">
              <div className="bg-emerald-600 text-white p-6 rounded-3xl shadow-2xl max-w-xl w-full pointer-events-auto border-4 border-emerald-800 flex items-center gap-4">
                 <CheckCircle size={48} className="text-emerald-200" />
                 <div><h2 className="text-xl font-black uppercase tracking-tight">System Stable</h2><p className="text-emerald-100 text-sm">You balanced your energy budget perfectly. Resilience increased.</p></div>
              </div>
           </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}